/**
 * Public / Open API error codes — snake_case, versioned independently of the
 * internal 6-char `FFAAEE` catalog. Pure + browser-safe. The translation from
 * internal codes to these lives in `./translate`.
 */
export const PublicErrorCode = {
  unauthorized: 'unauthorized',
  invalid_api_key: 'invalid_api_key',
  insufficient_scope: 'insufficient_scope',
  workspace_forbidden: 'workspace_forbidden',
  rate_limit_exceeded: 'rate_limit_exceeded',
  too_many_requests: 'too_many_requests',
  invalid_request: 'invalid_request',
  forbidden: 'forbidden',
  not_found: 'not_found',
  conflict: 'conflict',
  unprocessable_entity: 'unprocessable_entity',
  internal_error: 'internal_error',
  /**
   * The organization cannot hold any more mailboxes on its current plan.
   * Remedy: upgrade. Distinct from `warmup_limit_reached` below — both are 403s
   * that the status-default map would collapse into a bare `forbidden`, which is
   * why they are explicit overrides.
   */
  plan_limit_reached: 'plan_limit_reached',
  /**
   * The mailbox can be connected, but the organization is already warming as
   * many as its plan allows. Remedy: pause another mailbox, OR upgrade — a
   * strictly wider set of options than `plan_limit_reached`, which is the whole
   * reason the two must be tellable apart by a machine.
   */
  warmup_limit_reached: 'warmup_limit_reached',
  /** No verified partner callback integration on the organization. */
  partner_not_configured: 'partner_not_configured',
  /**
   * The supplied OAuth access token belongs to a different mailbox than the
   * `emailAddress` in the request. A 422 like a validation failure, but a
   * distinct code because the fix is not "correct the field" — it is "send the
   * token for the mailbox you named".
   */
  email_mismatch: 'email_mismatch',
  /**
   * A workspace budget allocation would take the organization's summed
   * allocations over its plan's daily warmup cap. A 400 like a malformed body,
   * but the payload is well-formed — the remedy is to lower another workspace's
   * budget (or upgrade), not to fix the request.
   */
  quota_exceeds_org_limit: 'quota_exceeds_org_limit',
  /**
   * The API key is valid but the organization's plan doesn't include API
   * access. A 403 like a scope or workspace rejection, but no key or request
   * change fixes it — the remedy is to upgrade the plan.
   */
  api_access_not_in_plan: 'api_access_not_in_plan',
  /**
   * The organization's subscription is paused, so no new mailbox can be
   * connected. A 403 like `plan_limit_reached`, but the remedy is to resume the
   * subscription, not to upgrade — and nothing changes about it by retrying.
   */
  subscription_paused: 'subscription_paused',
  /**
   * The mailbox's send-and-receive check is still running. A 409 like any
   * conflict, but nothing is wrong and nothing needs fixing: warmup starts by
   * itself when the check passes. Stop retrying and wait.
   */
  mailbox_verification_pending: 'mailbox_verification_pending',
  /**
   * The mailbox failed its send-and-receive check (a probe hard-bounced, or
   * never arrived). A 400 like a malformed request, but the request is fine —
   * the remedy is to fix the mailbox itself, then try again.
   */
  mailbox_verification_failed: 'mailbox_verification_failed',
  /**
   * A health re-check was requested for a mailbox checked less than 3 hours
   * ago. A 429 like `rate_limit_exceeded`, but nothing about the caller's
   * request rate is at fault and backing off API traffic fixes nothing: the
   * window is per MAILBOX and counts from its last check of any kind, including
   * the daily automatic one. `nextManualCheckAt` on the mailbox's DNS response
   * says when it opens.
   */
  recheck_too_soon: 'recheck_too_soon',
  /**
   * A health re-check was requested while that mailbox's previous one is still
   * running (its test emails are in flight). A 409 like `conflict`, but the
   * remedy is only to wait — up to ~4.5 hours — and the result then appears on
   * the same mailbox with no further call.
   */
  check_in_progress: 'check_in_progress',
} as const;

export type PublicErrorCode = (typeof PublicErrorCode)[keyof typeof PublicErrorCode];

/** Default public code per HTTP status, used when no explicit override applies. */
export const STATUS_TO_PUBLIC_CODE: Record<number, string> = {
  400: PublicErrorCode.invalid_request,
  401: PublicErrorCode.unauthorized,
  403: PublicErrorCode.forbidden,
  404: PublicErrorCode.not_found,
  409: PublicErrorCode.conflict,
  422: PublicErrorCode.unprocessable_entity,
  429: PublicErrorCode.rate_limit_exceeded,
  500: PublicErrorCode.internal_error,
};
